import { useState } from 'react'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuGroup, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@duck/ui'
import { Plus, Columns2 } from 'lucide-react'
import { workspaceTabsDefinitions, workspaceTabsOrder } from './workspaceTabs.definitions'
import { ChallengeService } from '@duck/api-definitions'
import { getTabClassName } from './WorkspaceTab'
import { useTar } from '@/modules/i18n'
import type { TranslationKey } from '@/modules/i18n'

export interface AddWorkspaceTabTabProps {
    onAddTab: (tabType: ChallengeService.WorkspaceTabTypeEnum) => void
    onOpenInSplitView?: (tabType: ChallengeService.WorkspaceTabTypeEnum) => void
    /** Tooltip for the split/other-view button: e.g. 'common.openInSplitView' or 'common.openInOtherView' */
    openInSplitViewTooltipKey?: TranslationKey
    className?: string
}

export const AddWorkspaceTabTab = ({ onAddTab, onOpenInSplitView, openInSplitViewTooltipKey = 'common.openInSplitView', className }: AddWorkspaceTabTabProps) => {
    const { t, isRtl } = useTar()
    const [isOpen, setIsOpen] = useState(false)

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <div
                    role="button"
                    tabIndex={0}
                    className={getTabClassName(false, false, true, className)}
                    style={{ zIndex: 1 }}
                >
                    <span className="relative z-10 pt-1">
                        <Plus className="size-4" />
                    </span>
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-48">
                <DropdownMenuGroup>
                    <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{t('common.newTab')}</DropdownMenuLabel>
                    {workspaceTabsOrder.map((tabType) => (
                        <div key={tabType} className="group flex items-center">
                            <DropdownMenuItem
                                onClick={() => onAddTab(tabType)}
                                className="flex-1 cursor-pointer hover:bg-transparent focus:bg-transparent hover:text-primary focus:text-primary"
                            >
                                {t(workspaceTabsDefinitions[tabType].label)}
                            </DropdownMenuItem>
                            {onOpenInSplitView && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                className="p-2 opacity-0 group-hover:opacity-50 hover:!opacity-100 transition-opacity hover:text-primary focus:text-primary cursor-pointer"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    onOpenInSplitView(tabType)
                                                    setIsOpen(false)
                                                }}
                                            >
                                                <Columns2 className="size-4" />
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side={isRtl ? 'left' : 'right'}>
                                            {t(openInSplitViewTooltipKey)}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                    ))}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
